/* eslint-disable max-lines */
import {
    NinchatAttributes,
    NinchatProperties,
} from '../../api/types'
import {
    NinchatState,
    NinchatStore,
} from 'api/types'
import {IdentityType} from 'api/types/properties'
import type {UiState} from 'state/ui'
import createCachedSelector from 're-reselect'
import {createSelector} from '@reduxjs/toolkit'
import {getActiveQueueRegisteredTicketCount} from 'utils/queueSettings'
import {memoizeWithArgs} from 'proxy-memoize'

export const getQueueAudiences = createCachedSelector(
    (state) => state.user_channels,
    (_: NinchatState, queue_id: NinchatProperties.QueueID) => queue_id,
    (channels, queue_id) => [...Object.entries(channels)]
        .filter(([, channel]) => channel.channel_attrs?.queue_id === queue_id && channel.channel_attrs?.audience_id)
        .map(([channel_id, channel]) => ({
            channel_id,
            ...channel,
        })),
)((_, queue_id) => queue_id)

export const getQueueTranscripts = createCachedSelector(
    (state) => state._user_audience_transcripts,
    (_: NinchatState, queue_id: NinchatProperties.QueueID) => queue_id,
    (transcripts, queue_id) => [...Object.entries(transcripts)]
        .filter(([, transcript]) => transcript.queue_id === queue_id && transcript.audience_id)
        .map(([, transcript]) => ({...transcript})),
)((_, queue_id) => queue_id)

export const getRealmsLegacy = createSelector(
    (state: NinchatState) => state._realms ?? {},
    (state: NinchatState) => state.user_realms,
    (_realms, user_realms) => [...Object.entries(user_realms), ...Object.entries(_realms)]
        .filter(([, realm]) => !!realm.realm_attrs)
        .map(([realm_id, realm]) => ({
            realm_id,
            ...realm,
        }))
        .sort((a, b) => ((a.realm_attrs.name ?? '') < (b.realm_attrs.name ?? '') ? -1 : (a.realm_attrs.name ?? '') > (b.realm_attrs.name ?? '') ? 1 : 0)),
)

export const getRealmAudiences = createCachedSelector(
    (state) => state.user_channels,
    (_: NinchatState, realm_id: NinchatProperties.RealmID) => realm_id,
    (channels, realm_id) => [...Object.entries(channels)]
        .filter(([, channel]) => channel.realm_id === realm_id && channel.channel_attrs?.audience_id)
        .map(([channel_id, channel]) => ({
            channel_id,
            ...channel,
        }))
        .sort((a, b) => (b.message_time ?? 0) - (a.message_time ?? 0)),
)((_, realm_id) => realm_id)

export const getRealmChannels = createCachedSelector(
    (state) => state.user_channels,
    (_: NinchatState, realm_id: NinchatProperties.RealmID) => realm_id,
    (channels, realm_id) => [...Object.entries(channels)]
        .filter(([, channel]) => channel.realm_id === realm_id && !channel.channel_attrs?.audience_id)
        .map(([channel_id, channel]) => ({
            channel_id,
            ...channel,
        }))
        .sort((a, b) => a.channel_attrs?.name?.localeCompare(b.channel_attrs?.name ?? '') ?? 0),
)((_, realm_id) => realm_id)

export const getRealmQueues = createCachedSelector(
    (state) => state.user_queues ?? {},
    (_: NinchatState, realm_id: NinchatProperties.RealmID) => realm_id,
    (queues, realm_id) => [...Object.entries(queues)]
        .filter(([, queue]) => queue.realm_id === realm_id)
        .map(([queue_id, queue]) => ({
            queue_id,
            ...queue,
        }))
        .sort((a, b) => ((a.queue_attrs.name ?? '') < (b.queue_attrs.name ?? '') ? -1 : (a.queue_attrs.name ?? '') > (b.queue_attrs.name ?? '') ? 1 : 0)),
)((_, realm_id) => realm_id)

export const getWritingMemberUserAttrs = memoizeWithArgs(
    (ninchat: NinchatState, channelId: NinchatProperties.ChannelID) => Object.entries(ninchat.user_channels)
        .filter(([channel_id]) => channel_id === channelId)
        .map(([, channel]) => [...Object.entries(channel.channel_members ?? {})])
        .flat()
        .filter(([userId]) => ninchat.user_id !== userId)
        .filter(([, member]) => !!member.member_attrs?.writing)
        .map(([userId]) => ({
            ...ninchat._users[userId]?.user_attrs,
            user_id: userId,
        })),
)

export const listActiveChannels = createSelector(
    (state: NinchatState) => state.user_channels || {},
    (channels) => [...Object.entries(channels)]
        .filter(([, channel]) => channel.channel_status && (!channel.channel_attrs?.closed && !channel.channel_attrs?.suspended))
        .sort(([, a], [, b]) => ((a.message_time || 0) < (b.message_time || 0) ? 1 : (a.message_time || 0) > (b.message_time || 0) ? -1 : 0))
        .map(([channel_id, channel]) => ({
            channel_id,
            ...channel,
        })),
)

type NativeActiveChannelList = {
    [key: NinchatProperties.RealmID]:
        {realm_id: NinchatProperties.RealmID} &
        NinchatStore.NinchatStoreRealm &
        {data: (
            {channel_id: NinchatProperties.ChannelID} &
            NinchatStore.NinchatStoreUserChannel
        )[]}
}

export const listActiveChannelsNative = createSelector(
    (state: NinchatState) => state.user_channels,
    (state: NinchatState) => state._realms,
    (state: NinchatState) => state.user_realms,
    (channels, _realms, user_realms) => Object.values(
        [...Object.entries(channels)]
            .filter(([, channel]) => channel.channel_status)
            .sort(([, a], [, b]) => ((a.message_time || 0) < (b.message_time || 0) ? 1 : (a.message_time || 0) > (b.message_time || 0) ? -1 : 0))
            .reduce((accumulator, [channel_id, channel]) => {
                const key = channel.realm_id

                if (!key) {
                    return accumulator
                }

                const realms = {
                    ..._realms,
                    ...user_realms,
                }

                accumulator[key] ||= {
                    realm_id: key,
                    ...realms[key],
                    data: [],
                }
                accumulator[key].data.push({
                    channel_id,
                    ...channel,
                })
                return accumulator
            }, {} as NativeActiveChannelList),
    ),
)

export const listActiveDialogues = createSelector(
    (state: NinchatState) => state.user_dialogues || {},
    (dialogues) => [...Object.entries(dialogues)]
        .filter(([key, value]) => {
            // https://github.com/somia/ninchat-app/issues/310
            const isDeprecatedDialogAudience = value.dialogue_members && value.dialogue_members[key] && value.dialogue_members[key].queue_id
            return !isDeprecatedDialogAudience
        })
        .filter(([, dialogue]) => ['highlight', 'unread'].includes(dialogue.dialogue_status ?? ''))
        .sort(([, a], [, b]) => ((a.message_time || 0) < (b.message_time || 0) ? 1 : (a.message_time || 0) > (b.message_time || 0) ? -1 : 0))
        .map(([dialogue_id, dialogue]) => ({
            dialogue_id,
            ...dialogue,
        })),
)

type NativeActiveDialogueList = {
    [key: number]:
        {data: (
            {dialogue_id: NinchatProperties.DialogueID} &
            NinchatProperties.NinchatUserDialogue &
            {user_attrs: NinchatAttributes.NinchatUserAttributes}
        )[]}
}

export const listActiveDialoguesNative = createSelector(
    (state: NinchatState) => state.user_dialogues,
    (state: NinchatState) => state._users,
    (dialogues, users) => Object.values(
        [...Object.entries(dialogues)]
            .filter(([dialogue_id, dialogue]) => dialogue_id in users && !['hidden', 'visible'].includes(dialogue.dialogue_status ?? ''))
            .sort(([, a], [, b]) => ((a.message_time || 0) < (b.message_time || 0) ? 1 : (a.message_time || 0) > (b.message_time || 0) ? -1 : 0))
            .reduce((accumulator, [dialogue_id, dialogue]) => {
                accumulator[0] ||= {data: []}
                accumulator[0].data.push({
                    dialogue_id,
                    ...dialogue,
                    user_attrs: users?.[dialogue_id]?.user_attrs,
                })
                return accumulator
            }, {} as NativeActiveDialogueList),
    ),
)

export const listActiveQueues = createSelector(
    (state: NinchatState) => state.user_queues ?? {},
    (_state: NinchatState, queuePickupDelays?: UiState['queue_pickup_delays']) => queuePickupDelays,
    (queues, queuePickupDelays) => [...Object.entries(queues)]
        .filter(([queueId, queue]) => {
            if (!queue.queue_attrs || queue.queue_attrs.group_id || queuePickupDelays?.[queueId]) {
                return false
            }

            // Group pickup stays delayed while any of its child queues is delayed.
            return !queue.queue_attrs.is_group || !Object.entries(queues).some(([childId, child]) => child.queue_attrs.group_id === queueId && queuePickupDelays?.[childId])
        })
        .map(([queueId, queue]) => { // combine data from grouped queues
            if (queue.queue_attrs.is_group) {
                const groupQueues = Object.entries(queues || [])
                    .filter(([, q]) => q.queue_attrs.group_id === queueId)
                    .map(([id, details]) => ({
                        id,
                        queueProps: details,
                    }))

                const queuesLength = groupQueues.reduce((acc, curr) => acc + (curr.queueProps.queue_attrs.length || 0), 0)
                const queueRegisteredContacts = groupQueues.reduce((acc, curr) => acc + getActiveQueueRegisteredTicketCount({
                    parentGroupQueue: queue,
                    queue: curr.queueProps,
                }), 0)
                return [queueId, {
                    ...queue,
                    queue_attrs: {
                        ...queue.queue_attrs,
                        length: queuesLength,
                        unassigned_registered: queueRegisteredContacts,
                    },
                    queue_settings: {...queue.queue_settings},
                }] as const
            }
            return [queueId, {
                ...queue,
                queue_attrs: {
                    ...queue.queue_attrs,
                    unassigned_registered: getActiveQueueRegisteredTicketCount({queue}),
                },
            }] as const
        })
        .filter(([, queue]) => queue.queue_attrs.length || (queue.queue_attrs.unassigned_registered ?? 0) > 0) // eslint-disable-line no-extra-parens
        .sort(([, a], [, b]) => ((a.queue_attrs.name ?? '') < (b.queue_attrs.name ?? '') ? -1 : (a.queue_attrs.name ?? '') > (b.queue_attrs.name ?? '') ? 1 : 0))
        .map(([queue_id, queue]) => ({
            queue_id,
            ...queue,
        })),
)

type NativeActiveQueueList = {
    [key: NinchatProperties.RealmID]:
        {realm_id: NinchatProperties.RealmID} &
        NinchatAttributes.NinchatRealmAttributes &
        {data: (
            {queue_id: NinchatProperties.QueueID} &
            NinchatProperties.NinchatUserQueue
        )[]}
}

export const listActiveQueuesNative = createSelector(
    (state: NinchatState) => state.user_queues ?? {},
    (state: NinchatState) => state._realms,
    (state: NinchatState) => state.user_realms,
    (queues, _realms, user_realms) => Object.values(
        [...Object.entries(queues)]
            .filter(([, queue]) => queue.queue_attrs.length)
            .sort(([, a], [, b]) => ((a.queue_attrs.name ?? '') < (b.queue_attrs.name ?? '') ? -1 : (a.queue_attrs.name ?? '') > (b.queue_attrs.name ?? '') ? 1 : 0))
            .reduce((accumulator, [queue_id, queue]) => {
                const key = queue.realm_id

                if (!key) {
                    return accumulator
                }

                const realms = {
                    ..._realms,
                    ...user_realms,
                }

                accumulator[key] ||= {
                    realm_id: key,
                    ...realms[key],
                    data: [],
                }
                accumulator[key].data.push({
                    queue_id,
                    ...queue,
                })
                return accumulator
            }, {} as NativeActiveQueueList),
    ).sort((a, b) => ((a.name ?? '') < (b.name ?? '') ? -1 : (a.name ?? '') > (b.name ?? '') ? 1 : 0)),
)

export const listDialogues = createSelector(
    (state: NinchatState) => state.user_dialogues,
    (dialogues) => [...Object.entries(dialogues)]
        .filter(([key, value]) => {
            // https://github.com/somia/ninchat-app/issues/310
            const isDeprecatedDialogAudience = value.dialogue_members && value.dialogue_members[key] && value.dialogue_members[key].queue_id
            const isHidden = value.dialogue_status === 'hidden'
            return !isDeprecatedDialogAudience && !isHidden
        })
        .sort(([, a], [, b]) => ((a.message_time || 0) < (b.message_time || 0) ? 1 : (a.message_time || 0) > (b.message_time || 0) ? -1 : 0))
        .map(([dialogue_id, dialogue]) => ({
            dialogue_id,
            ...dialogue,
        })),
)

export const listHighlightingChannels = createSelector(
    (state: NinchatState) => state.user_channels || {},
    (channels) => [...Object.entries(channels)]
        .filter(([, channel]) => channel.channel_status === 'highlight' && (!channel.channel_attrs?.closed && !channel.channel_attrs?.suspended))
        .sort(([, a], [, b]) => ((a.message_time || 0) < (b.message_time || 0) ? 1 : (a.message_time || 0) > (b.message_time || 0) ? -1 : 0))
        .map(([channel_id, channel]) => ({
            channel_id,
            ...channel,
        })),
)

export const getActivityCount = createSelector(
    listActiveChannels,
    listActiveDialogues,
    listActiveQueues,
    (c, d, q) => c.length + d.length + q.length,
)

export const getHighlightCount = createSelector(
    listHighlightingChannels,
    listActiveDialogues,
    listActiveQueues,
    (c, d, q) => c.length + d.length + q.length,
)

export const getIdentities = createSelector(
    ({ninchat}: {ninchat: NinchatState}) => ninchat.user_identities || {},
    (identities) => {
        const prepareItems = (items: {
            [key: string]: NinchatAttributes.NinchatIdentityAttributes;
        }, type: IdentityType) => Object.entries(items || {}).map(([key, value]) => ({
            attrs: value,
            name: key,
            type,
        }))

        return [
            ...prepareItems(identities.email || {}, 'email'),
            ...prepareItems(identities.apns || {}, 'apns'),
            ...prepareItems(identities.gcm || {}, 'gcm'),
        ]
    },
)

export const getRealmMembers = createSelector([
    ({ninchat}: {ninchat: NinchatState}) => ninchat,
    (_state, realmId) => realmId,
],
(ninchat, realmId) => {
    const {
        user_realms: userRealms, _users: users, user_id: userId, user_attrs: userAttrs, user_identities: userIdentities,
    } = ninchat

    const realmMembersObj = userRealms[realmId]?.realm_members || {}
    const realmMembers = Object.entries(realmMembersObj)
        .filter(([_key, value]) => Object.keys(value).length > 0)
        .map(([key, value]) => {
            const identities = users[key]?.user_identities || (key === userId ? userIdentities : undefined)
            return {
                details: users[key]?.user_attrs || (key === userId ? userAttrs : {}),
                id: key,
                permissions: value.member_attrs?.permissions || [],
                ...identities && {identities},
            }
        })

    return realmMembers
})


export const getChannelMembers = createSelector([
    ({ninchat}: {ninchat: NinchatState}) => ninchat,
    (_state, channelId) => channelId,
],
(ninchat, channelId) => {
    const {_users: users} = ninchat

    return Object.entries(ninchat?.user_channels?.[channelId]?.channel_members ?? {})
        .map(([key, _value]) => ({
            details: users[key]?.user_attrs,
            id: key,
            member_attrs: _value.member_attrs || undefined,
        }))
})
